import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  RefreshCw, 
  ArrowRight, 
  ShieldAlert
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function UserPendingActions() {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPendingActions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/pending-actions', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load pending actions');
      const data = await res.json();
      if (data.success) {
        setActions(data.data || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingActions();
  }, [session]);

  const handleOpenCase = (caseId: string) => {
    navigate(`/user/in-progress?focus=${caseId}`);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-rose-500" />
            <span>Pending Workflow Actions</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Workflow tasks and governance gates currently queued requiring your identity's intervention.
          </p>
        </div>

        <button
          onClick={fetchPendingActions}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ACTION ITEMS */}
      {loading ? (
        <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3 rounded-xl border border-border bg-card">
          <RefreshCw className="h-6 w-6 animate-spin text-rose-500" />
          <span className="text-xs">Checking for pending actions...</span>
        </div>
      ) : actions.length === 0 ? (
        <div className="p-16 text-center text-muted-foreground rounded-xl border border-border bg-card">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500/60 mb-3" />
          <p className="text-sm font-medium text-foreground">All Clear! No Pending Actions Required</p>
          <p className="text-xs mt-1">You have zero blocking tasks or clarification requests on your SOC detection use cases.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {actions.map((act) => (
            <div 
              key={act.id} 
              onClick={() => handleOpenCase(act.id)}
              className="group flex flex-col justify-between p-5 rounded-xl border border-rose-500/30 bg-card hover:bg-muted/30 cursor-pointer shadow-xs transition-all hover:border-rose-500"
            >
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold text-xs text-rose-500">
                    {act.number}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/10 text-rose-500 border border-rose-500/20">
                    Step {act.current_step}: {act.status}
                  </span>
                </div>

                <h3 className="text-sm font-bold text-foreground mt-2 group-hover:text-rose-500 transition-colors line-clamp-1">
                  {act.title}
                </h3>

                <div className="mt-3 p-3 rounded-lg bg-rose-500/5 border border-rose-500/15 flex items-start gap-2.5">
                  <ShieldAlert className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <span className="font-semibold text-rose-600 block">Action Required:</span>
                    <span className="text-muted-foreground">{act.action_required}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground text-[11px]">
                  Priority: <strong className="text-foreground">{act.priority}</strong>
                </span>
                <span className="flex items-center gap-1 font-semibold text-rose-500 group-hover:underline">
                  <span>Open Related Use Case</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
